import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Box, Button, Card, Grid, Group, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconAffiliate, IconUserPlus, IconUsers, IconWand } from '@tabler/icons-react';

// Replaces the old bare "No personas yet" dashed box. Teaches the product
// with a labeled sample card instead of just prompting for input, per the
// dashboard redesign review.
const STEPS = [
  { icon: IconWand, title: 'Build a persona', body: "Add the traits, interests, and quirks of the person you're shopping for." },
  { icon: IconAffiliate, title: 'Get graph-powered matches', body: 'Neo4j finds gifts from overlapping-but-unusual trait combinations, not just popularity.' },
  { icon: IconUsers, title: 'Collaborate on the gift', body: 'Invite friends or family to split the cost or vote on the pick for group occasions.' }
];

const EmptyDashboardState = () => {
  const navigate = useNavigate();

  return (
    <Stack gap="xl">
      <Card radius="lg" p="xl" withBorder>
        <Grid gutter="xl" align="center">
          <Grid.Col span={{ base: 12, sm: 7 }}>
            <Title order={2} size="h3" mb={8}>Build your first persona</Title>
            <Text c="dimmed" size="sm" mb="lg" maw={440}>
              A persona is more than a wishlist &mdash; likes, dislikes, and traits feed the
              graph engine so it can surface gifts nobody would think to search for.
              Takes about 2 minutes.
            </Text>
            <Button
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
              onClick={() => navigate('/personas')}
            >
              Create your first persona
            </Button>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <Card
              radius="md"
              p="md"
              style={{ borderStyle: 'dashed', borderWidth: 1.5 }}
              withBorder
            >
              <Badge size="xs" variant="filled" color="gray" mb="sm">
                EXAMPLE
              </Badge>
              <Group gap="sm" mb="sm">
                <Avatar radius="xl" size={40} variant="gradient" gradient={{ from: 'blue', to: 'cyan', deg: 45 }}>
                  SC
                </Avatar>
                <Box>
                  <Text fw={600} size="sm">Sarah Chen</Text>
                  <Text size="xs" c="dimmed">Sister</Text>
                </Box>
              </Group>
              <Group gap={6}>
                {['Hiking', 'Film photography', 'Coffee'].map(tag => (
                  <Badge key={tag} size="xs" variant="light" color="gray">{tag}</Badge>
                ))}
              </Group>
            </Card>
          </Grid.Col>
        </Grid>
      </Card>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        {STEPS.map((step, i) => (
          <Card key={step.title} radius="lg" p="md" withBorder>
            <ThemeIcon size={30} radius="md" variant="light" color="blue" mb="sm">
              <step.icon size={16} />
            </ThemeIcon>
            <Text fw={600} size="sm" mb={4}>{i + 1}. {step.title}</Text>
            <Text size="xs" c="dimmed">{step.body}</Text>
          </Card>
        ))}
      </SimpleGrid>

      <Card radius="lg" p="md" withBorder>
        <Group justify="space-between" wrap="wrap" gap="md">
          <Group gap="sm">
            <ThemeIcon size={40} radius="xl" variant="light" color="orange">
              <IconUserPlus size={20} />
            </ThemeIcon>
            <Box>
              <Text fw={600} size="sm">Gift-giving is better together</Text>
              <Text size="xs" c="dimmed">Invite friends or family to plan and split group gifts.</Text>
            </Box>
          </Group>
          <Button variant="light" onClick={() => navigate('/friends')}>Invite people</Button>
        </Group>
      </Card>
    </Stack>
  );
};

export default EmptyDashboardState;
